import { httpsCallable } from 'firebase/functions';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { functions, db, auth } from '../firebase';
import { buildAssistantContext, buildContextBlock } from './runAssistant';
import { normalizeVehicleType, TIPOS_VEHICULO, crearComentarioDeSoporte } from './tools';
import { snapshotMigrationConfig } from '../utils/migrationConfig';
import { EMERGENCIAS_ACTIVAS } from '../utils/featureFlags';
import {
  createDiagnosis,
  createFuelLog as apiCreateFuelLog,
  createRecord as apiCreateRecord,
  createVehicle as apiCreateVehicle,
  recordApiBody,
  setVehicleMileage,
  vehicleApiBody,
} from '../api/vehicles';

// Controlador de la VOZ EN VIVO (Gemini Live) como agente de servicio al cliente.
// Vive a nivel global (AssistantFab) para que la llamada sobreviva al navegar.

const DEST_ROUTES = {
  vehicles: '/vehicles', appointments: '/appointments', marketplace: '/marketplace',
  orders: '/my-orders', mechanics: '/mechanics', profile: '/dashboard', home: '/home', asistencia: '/asistencia',
};

/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ 🔴 UN FALLO TIENE QUE LLEGARLE AL MODELO, Y TIENE QUE SER INEQUÍVOCO.     ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * Un modelo al que no le llega el resultado de una acción no se calla: RELLENA
 * el hueco narrando el éxito («listo, registré tu Corolla») sobre algo que no
 * ocurrió. Y quien no encuentra ese carro en su garaje deja de creerle a la app
 * entera. Por eso todo fallo se devuelve con esta orden dentro: la instrucción
 * del sistema se puede desobedecer, un resultado recibido no se puede inventar.
 */
export const ORDEN_NO_AFIRMAR =
  'NO SE GUARDÓ NADA. Dile al usuario que no se pudo y explícale el motivo. NUNCA afirmes que quedó hecho.';

/** El fallo de una herramienta, con la orden dentro. */
export function fallo(motivo) {
  return { ok: false, error: motivo || 'error', instruccion: ORDEN_NO_AFIRMAR };
}

// Herramientas que la voz puede EJECUTAR. Las de "navegar" te llevan a la pantalla
// (la llamada sigue activa); las de "escribir" se completan hablando.
// EMERGENCIAS_ACTIVAS=false (relanzamiento): la voz no ofrece solicitarAsistencia ni el
// destino «asistencia». Para restaurarlas basta encender el flag en featureFlags.js.
const LIVE_TOOLS_TODAS = [
  { name: 'actualizarKilometraje', description: 'Actualiza el kilometraje (odómetro) actual de un vehículo.',
    parameters: { type: 'OBJECT', properties: { vehiculoId: { type: 'STRING' }, km: { type: 'NUMBER' } }, required: ['km'] } },
  { name: 'registrarCombustible', description: 'Registra un tanqueo: galones echados y kilómetros recorridos desde el último.',
    parameters: { type: 'OBJECT', properties: { vehiculoId: { type: 'STRING' }, galones: { type: 'NUMBER' }, kmRecorridos: { type: 'NUMBER' } }, required: ['galones', 'kmRecorridos'] } },
  { name: 'registrarMantenimiento', description: 'Registra un servicio de mantenimiento hecho al carro (tipo, costo RD$, kilometraje).',
    parameters: { type: 'OBJECT', properties: { vehiculoId: { type: 'STRING' }, tipo: { type: 'STRING' }, costo: { type: 'NUMBER' }, km: { type: 'NUMBER' } }, required: ['tipo'] } },
  { name: 'guardarDiagnostico', description: 'Guarda en el historial un diagnóstico que YA conversaste (síntoma + causas + recomendación).',
    parameters: { type: 'OBJECT', properties: { vehiculoId: { type: 'STRING' }, resumen: { type: 'STRING' } }, required: ['resumen'] } },
  { name: 'agendarCita', description: 'Inicia el agendado de una cita: pre-selecciona vehículo y servicio y lleva al usuario a elegir el mecánico.',
    parameters: { type: 'OBJECT', properties: { vehiculoId: { type: 'STRING' }, servicio: { type: 'STRING' } } } },
  { name: 'solicitarAsistencia', description: 'Abre la solicitud de asistencia en carretera con el tipo pre-seleccionado.',
    parameters: { type: 'OBJECT', properties: { tipo: { type: 'STRING', enum: ['grua', 'gruaMoto', 'jompeo', 'cerrajero', 'goma'] } }, required: ['tipo'] } },
  { name: 'buscarRepuesto', description: 'Busca repuestos/productos en el Marketplace, opcionalmente filtrando por el vehículo.',
    parameters: { type: 'OBJECT', properties: { texto: { type: 'STRING' }, vehiculoId: { type: 'STRING' } }, required: ['texto'] } },
  { name: 'irA', description: 'Lleva al usuario a una sección de la app.',
    parameters: { type: 'OBJECT', properties: { destino: { type: 'STRING', enum: ['vehicles', 'appointments', 'marketplace', 'orders', 'mechanics', 'profile', 'home', 'asistencia'] } }, required: ['destino'] } },
  { name: 'enviarComentario', description: 'Envía al equipo de MekaFix una QUEJA/reclamo o una SUGERENCIA del usuario sobre el servicio o la app. Abre un caso de soporte que el equipo lee y responde. Úsalo cuando el usuario se queja, reclama, protesta o propone una mejora. NO es para diagnosticar el carro ni para pedir asistencia.',
    parameters: { type: 'OBJECT', properties: { tipo: { type: 'STRING', enum: ['queja', 'sugerencia'] }, texto: { type: 'STRING', description: 'Lo que el usuario quiere decirle al equipo, en sus propias palabras.' } }, required: ['tipo', 'texto'] } },
  { name: 'guardarTratamiento', description: 'Guarda cómo el usuario prefiere que nos dirijamos a él (Sr., Sra., Ing., Dr., Lic., etc.).',
    parameters: { type: 'OBJECT', properties: { tratamiento: { type: 'STRING', enum: ['sr', 'sra', 'srta', 'lic', 'ing', 'dr', 'dra', 'arq', 'prof', 'none'] } }, required: ['tratamiento'] } },
  // ⚠️ El enum de `tipo` SALE de `TIPOS_VEHICULO`, que es el vocabulario de la
  // app y de la base. La lista escrita a mano que había aquí («sedan»,
  // «hatchback», «coupe», «4wd»…) no existía en ningún sitio y devolvía 400.
  { name: 'registrarVehiculo', description: 'Registra un vehículo NUEVO del usuario. Marca y modelo obligatorios; año, kilometraje y tipo si el usuario los da. El tipo es OPCIONAL: si no lo sabes, registra igual en vez de dejar al usuario sin su carro. Equivalencias: "auto" cubre sedán, hatchback y coupé; "suv" es jeepeta; "camion_pes" es camión pesado o patana.',
    parameters: { type: 'OBJECT', properties: { marca: { type: 'STRING' }, modelo: { type: 'STRING' }, ano: { type: 'NUMBER' }, km: { type: 'NUMBER' }, tipo: { type: 'STRING', enum: TIPOS_VEHICULO } }, required: ['marca', 'modelo'] } },
];
export const LIVE_TOOLS = EMERGENCIAS_ACTIVAS
  ? LIVE_TOOLS_TODAS
  : LIVE_TOOLS_TODAS
    .filter((tool) => tool.name !== 'solicitarAsistencia')
    .map((tool) => (tool.name === 'irA'
      ? { ...tool, parameters: { ...tool.parameters, properties: { destino: { type: 'STRING', enum: tool.parameters.properties.destino.enum.filter((d) => d !== 'asistencia') } } } }
      : tool));

// Fragmentos de la instrucción que mencionan Emergencias: vacíos mientras estén ocultas.
const TUTORIAL_ASISTENCIA = EMERGENCIAS_ACTIVAS ? '\n - Asistencia en carretera: grúa, goma (llanta), jompeo (paso de corriente) y cerrajero.' : '';
const EJECUTAR_ASISTENCIA = EMERGENCIAS_ACTIVAS ? 'solicitarAsistencia (grúa/jompeo/cerrajero/goma), ' : '';
const PANTALLA_ASISTENCIA = EMERGENCIAS_ACTIVAS ? 'agendar/asistencia/comprar' : 'agendar/comprar';

function resolveV(ctx, id) {
  return (ctx.vehicles || []).find((x) => x.id === id) || (ctx.vehicles?.length === 1 ? ctx.vehicles[0] : null);
}

export async function execLiveTool(name, args, ctx, navigate) {
  const uid = auth.currentUser?.uid;
  // Navegación (no requieren resolver carro estricto)
  if (name === 'solicitarAsistencia') {
    navigate('/emergency-request', { state: { serviceType: args.tipo } });
    return { ok: true, mensaje: `Abrí la solicitud de asistencia (${args.tipo}). Termina en la pantalla.` };
  }
  if (name === 'buscarRepuesto') {
    const v = resolveV(ctx, args.vehiculoId);
    navigate('/marketplace', { state: { search: args.texto, vehicleId: v?.id || null } });
    return { ok: true, mensaje: `Busqué "${args.texto}" en el Marketplace.` };
  }
  if (name === 'irA') {
    navigate(DEST_ROUTES[args.destino] || '/home');
    return { ok: true, mensaje: 'Listo, te llevé ahí.' };
  }
  if (name === 'enviarComentario') {
    if (!uid) return fallo('No autenticado');
    const texto = String(args.texto ?? '').trim();
    if (!texto) return fallo('No tengo el texto del comentario. Pregúntale qué quiere decirle al equipo.');
    let clase;
    try {
      // El asunto del caso es texto de cliente (lo ve él en "Mis casos"), así
      // que sale del locale. La voz no tiene el `t` de React: se pide la MISMA
      // instancia de i18n que usa el resto de la app, y se pide AQUÍ y no al
      // cargar el módulo para no atar el controlador de la llamada a la
      // inicialización de i18n.
      const { default: i18n } = await import('../i18n');
      clase = await crearComentarioDeSoporte(
        { tipo: args.tipo, texto, profile: ctx.profile }, (k, o) => i18n.t(k, o),
      );
    } catch (e) {
      console.error('enviarComentario:', e);
      return fallo('No se pudo registrar el comentario. NO se envió nada al equipo.');
    }
    return { ok: true, mensaje: clase === 'sugerencia'
      ? 'Sugerencia registrada. El equipo de MekaFix la revisa y él puede seguirla en Ayuda, Mis casos.'
      : 'Queja registrada. El equipo de MekaFix la revisa y él puede seguirla en Ayuda, Mis casos.' };
  }
  if (name === 'guardarTratamiento') {
    if (uid) { try { await updateDoc(doc(db, 'users', uid), { honorific: args.tratamiento, honorificAsked: true }); } catch (_) {} }
    return { ok: true, mensaje: 'Perfecto, guardé cómo dirigirme a ti.' };
  }
  if (name === 'registrarVehiculo') {
    if (!uid) return fallo('No autenticado');
    if (!args.marca || !args.modelo) return fallo('Falta marca o modelo. Pídelos.');
    // 🔴 REGISTRAR EL CARRO EN LA BASE QUE YA NO SE LEE es el peor fallo posible
    // de esta herramienta: la voz contesta «listo, registré tu Corolla» y el
    // dueño no lo encuentra nunca en su garaje.
    //
    // 🔴 Y el tipo se TRADUCE antes de viajar. Ver `normalizeVehicleType`: la
    // palabra que dicta el modelo no puede tumbar el alta entera.
    const tipo = normalizeVehicleType(args.tipo);
    const nuevo = {
      brand: args.marca, model: args.modelo,
      year: args.ano ? Number(args.ano) : null,
      currentMileage: args.km ? Number(args.km) : 0,
      ...(tipo ? { vehicleType: tipo } : {}),
    };
    let ref;
    try {
      ref = (await snapshotMigrationConfig().catch(() => null))?.readsFrom('vehicles') === 'postgres'
        // El API no acepta `userId` (el dueño de un carro no se elige) ni las
        // fechas: las pone la base. Lo dice con un 400 que nombra el campo.
        ? await apiCreateVehicle(vehicleApiBody(nuevo, { create: true }))
        : await addDoc(collection(db, 'vehicles'), {
            userId: uid, ...nuevo, createdAt: new Date(), updatedAt: new Date(),
          });
    } catch (e) {
      console.error('registrarVehiculo:', e);
      return fallo(`No se pudo registrar el vehículo: ${e?.message || 'error'}`);
    }
    // Agrégalo al contexto para que las siguientes acciones puedan usarlo.
    try { (ctx.vehicles = ctx.vehicles || []).push({ id: ref.id, brand: args.marca, model: args.modelo, year: args.ano || null, currentMileage: args.km || 0, vehicleType: tipo }); } catch (_) {}
    return { ok: true, mensaje: `Listo, registré tu ${args.marca} ${args.modelo}${args.ano ? ' ' + args.ano : ''}.` };
  }
  if (name === 'agendarCita') {
    const v = resolveV(ctx, args.vehiculoId);
    try { sessionStorage.setItem('appointmentContext', JSON.stringify({ vehicleId: v?.id || '', issue: args.servicio || '' })); } catch (_) {}
    navigate('/mechanics');
    return { ok: true, mensaje: `Te llevé a elegir el mecánico${args.servicio ? ` para "${args.servicio}"` : ''}. Elige y confirma ahí.` };
  }

  // Escrituras (requieren carro)
  const v = resolveV(ctx, args.vehiculoId);
  if (!v) return fallo('No identifiqué el vehículo. Pregunta cuál.');
  // Las tres escrituras de abajo comparten interruptor: es el mismo expediente.
  const enPostgres = (await snapshotMigrationConfig().catch(() => null))?.readsFrom('vehicles') === 'postgres';
  if (name === 'actualizarKilometraje') {
    if (enPostgres) {
      // El odómetro tiene RUTA PROPIA y es POST, no PATCH: mezclarlo con la
      // ficha dejaría que cualquier guardado lo pisara, y el verbo equivocado
      // no da 405 sino un 404 que parece «ese carro no existe».
      //
      // ⚠️ `isCorrection` NO se manda, y es a propósito: el API solo acepta un
      // kilometraje a la baja si quien escribe DECLARA que está corrigiendo, y
      // por voz no hay forma de confirmarlo. Un número dictado más bajo que el
      // actual se rechaza con su motivo, que es mejor que dar por hecho que el
      // dueño quiso corregir su odómetro hacia atrás sin decirlo.
      await setVehicleMileage(v.id, { km: Number(args.km) });
    } else {
      await updateDoc(doc(db, 'vehicles', v.id), { currentMileage: Number(args.km) });
    }
    return { ok: true, mensaje: `Kilometraje del ${v.brand} ${v.model} actualizado a ${Number(args.km).toLocaleString()} km.` };
  }
  if (name === 'registrarCombustible') {
    const eff = Number(args.galones) > 0 ? Number(args.kmRecorridos) / Number(args.galones) : 0;
    if (enPostgres) {
      // El rendimiento lo calcula el SERVIDOR, y la marca/modelo/año no viajan:
      // están en el carro, y repetirlos en cada tanqueo es lo que hace que un
      // día dejen de coincidir. Aquí `eff` solo se usa para contestar en voz.
      await apiCreateFuelLog(v.id, {
        kmDriven: Number(args.kmRecorridos),
        gallons: Number(args.galones),
        condition: 'MIXED',
      });
    } else {
      await addDoc(collection(db, 'fuelLogs'), {
        vehicleId: v.id, userId: uid, brand: v.brand, model: v.model, year: v.year,
        kmDriven: Number(args.kmRecorridos), gallons: Number(args.galones), efficiency: eff, condition: 'mixed', createdAt: new Date(),
      });
    }
    return { ok: true, mensaje: `Tanqueo registrado. Rendimiento: ${eff.toFixed(1)} km por galón.` };
  }
  if (name === 'registrarMantenimiento') {
    const servicio = {
      serviceType: args.tipo,
      // Pesos: el adaptador lo pasa a centavos. Mandarlo ya multiplicado
      // guardaría cien veces lo que el dueño acaba de decir en voz alta.
      cost: Number(args.costo) || 0,
      mileageAtService: Number(args.km) || v.currentMileage || 0,
      serviceDate: new Date(),
      performedBy: 'voice-assistant',
    };
    if (enPostgres) {
      await apiCreateRecord(v.id, recordApiBody(servicio));
    } else {
      await addDoc(collection(db, 'maintenanceRecords'), {
        vehicleId: v.id, ...servicio, createdAt: new Date(),
      });
    }
    return { ok: true, mensaje: `Mantenimiento "${args.tipo}" registrado para el ${v.brand} ${v.model}.` };
  }
  if (name === 'guardarDiagnostico') {
    if (!uid) return fallo('No autenticado');
    try {
      // Un resumen hablado es UN mensaje: se abre la conversación con él. Va por
      // la misma ruta que el chat para que los tres escritores dejen la misma
      // forma en el expediente del carro.
      const snap = await snapshotMigrationConfig();
      if (snap.readsFrom('vehicles') === 'postgres') {
        await createDiagnosis(v.id, { mode: 'diy', messages: [{ role: 'assistant', content: args.resumen || '' }] });
      } else {
        await addDoc(collection(db, 'aiDiagnoses'), {
          userId: uid, vehicleId: v.id, mode: 'diy', messages: [{ role: 'assistant', content: args.resumen || '' }], createdAt: new Date(), updatedAt: new Date(),
        });
      }
    } catch (e) {
      console.error('guardarDiagnostico:', e);
      return fallo('No pude guardar el diagnóstico en el historial.');
    }
    return { ok: true, mensaje: `Diagnóstico guardado en el historial del ${v.brand} ${v.model}.` };
  }
  return fallo('Herramienta desconocida.');
}

/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ EJECUTA LO QUE PIDIÓ EL MODELO Y LE DEVUELVE SIEMPRE EL RESULTADO.        ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * Antes esto vivía dentro del `onmessage` de la sesión y terminaba en
 * `try { r.session?.sendToolResponse(...) } catch (_) {}`: la ÚNICA entrega de
 * la verdad era optativa (`?.`) y su fallo se tragaba en silencio. Si no salía,
 * nadie se enteraba —ni el modelo, ni el usuario, ni la consola— y el modelo
 * rellenaba el hueco afirmando un éxito que no ocurrió.
 *
 * Ahora: si la entrega falla se avisa por el OTRO canal (un turno de texto, que
 * es el único camino que queda hacia el modelo) y se le dice al usuario en
 * pantalla. Vive fuera de la sesión para poder probarse.
 */
export async function responderLlamadas(fcs, { ctx, navigate, session, onLine } = {}) {
  const functionResponses = [];
  for (const fc of fcs || []) {
    let result;
    try {
      // eslint-disable-next-line no-await-in-loop
      result = await execLiveTool(fc.name, fc.args || {}, ctx, navigate);
    } catch (e) {
      console.error('execLiveTool:', e);
      result = fallo(e?.message || 'error');
    }
    // Cinturón: cualquier fallo sale con su orden, aunque quien lo produjo la
    // haya olvidado. Un `ok:false` pelado se lee como un dato más.
    if (!result || result.ok !== true) result = fallo((result && result.error) || 'error');
    functionResponses.push({ id: fc.id, name: fc.name, response: { result } });
  }

  try {
    session.sendToolResponse({ functionResponses });
    return { entregado: true, functionResponses };
  } catch (e) {
    console.error('sendToolResponse:', e);
    let avisado = false;
    try {
      session.sendClientContent({
        turns: [{ role: 'user', parts: [{ text:
          `AVISO DEL SISTEMA: no se pudo entregar el resultado de ${functionResponses.map(f => f.name).join(', ') || 'la acción'}. ${ORDEN_NO_AFIRMAR}` }] }],
        turnComplete: true,
      });
      avisado = true;
    } catch (e2) {
      console.error('aviso al modelo:', e2);
    }
    // El usuario tiene que enterarse aunque el modelo siga hablando: es la
    // única señal de que lo que va a oír puede no haber pasado.
    try { onLine && onLine('No se pudo confirmar la acción. Revísala en la app antes de darla por hecha.'); } catch (_) {}
    return { entregado: false, avisado, functionResponses };
  }
}

// ── Audio helpers ────────────────────────────────────────────────────────────
function floatTo16kPcmBase64(float32, srcRate) {
  const ratio = srcRate / 16000;
  const outLen = Math.floor(float32.length / ratio);
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const s = Math.max(-1, Math.min(1, float32[Math.floor(i * ratio)] || 0));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  const bytes = new Uint8Array(out.buffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}
function playPcm24(ctx, b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const int16 = new Int16Array(bytes.buffer);
  const f32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i++) f32[i] = int16[i] / 0x8000;
  const buf = ctx.createBuffer(1, f32.length, 24000);
  buf.getChannelData(0).set(f32);
  const node = ctx.createBufferSource();
  node.buffer = buf; node.connect(ctx.destination);
  const t = Math.max(ctx.currentTime, ctx._nextTime || 0);
  node.start(t); ctx._nextTime = t + buf.duration;
}

// Crea una sesión de voz. Llamar start() DENTRO del gesto del usuario (iOS).
const TUTORIAL_EXTRA = `

MODO TUTORIAL (bienvenida a un usuario NUEVO). Haz un recorrido COMPLETO pero ágil. Una frase por punto, sin saltarte ninguno:
(1) Preséntate con calidez como su asistente MekaFix y pregúntale cómo prefiere que le llamemos (Sr., Sra., Srta., Ing., Dr., Dra., Lic., Arq., Prof., o ninguno). Guárdalo con guardarTratamiento.
(2) Recorre TODAS las funciones, una por una:
 - Registrar su carro y ver la SALUD del vehículo (score y alertas).
 - Registrar kilometraje, tanqueos de combustible y mantenimientos.
 - Diagnóstico con IA: le explicas cualquier síntoma por voz o texto.
 - Buscar mecánicos y talleres, y agendar citas.${TUTORIAL_ASISTENCIA}
 - Marketplace para comprar repuestos.
 - Que puede hablar contigo por voz cuando quiera, mientras tenga saldo.
(3) Ofrécele registrar su PRIMER carro ahora mismo: pídele marca, modelo, año y kilometraje, y regístralo con registrarVehiculo.
(4) Cuando hayas cubierto TODO, dile claramente que el tour terminó, que ya puede usar la app y que te pregunte lo que quiera cuando quiera.
Sé cálido y ágil. Cubre TODOS los puntos ANTES de cerrar; no termines a la mitad. Invítalo a interrumpirte con preguntas.`;

// Recorte defensivo de la semilla. Quien la manda ya la recorta, pero esta
// instrucción viaja ENTERA en cada conexión: una conversación larga la empujaría
// hasta desplazar las reglas de honestidad. Se conserva el FINAL, no el
// principio: lo que el usuario quiere continuar es lo último que dijo.
const SEED_MAX = 4000;

/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ LA INSTRUCCIÓN DEL SISTEMA DE LA VOZ, FUERA DE `start()`.                 ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * Vive en una función pura para poder comprobarse: dentro de `start()` se arma
 * detrás del micrófono, del token efímero y del WebSocket, así que no había
 * forma de leer qué se le dice al modelo sin levantar media sesión de audio.
 *
 * ⚠️ ANCLA, NO ADORNO: `instruccionHonesta.test.js` no importa este módulo — lee
 * el FUENTE desde la primera marca `systemInstruction:` hasta el bloque de
 * respuestas de la sesión, y comprueba ahí que las reglas de HONESTIDAD y
 * ANTI-INVENCIÓN siguen escritas. Esa marca es la línea de aquí arriba, así que
 * el literal tiene que quedar POR DEBAJO de ella. Si algún día se mueve más
 * abajo en el archivo, esa prueba se pondría verde mirando texto vacío.
 */
export function buildLiveInstruction({ contextBlock = '', tutorial = false, seedContext = '' } = {}) {
  const base = `Eres el asistente de voz de MekaFix, como un agente de SERVICIO AL CLIENTE que RESUELVE en la cuenta del usuario. App de gestión de vehículos en República Dominicana. Habla español dominicano PROFESIONAL y cortés, claro y en frases breves. Nada de jerga callejera.

CONOCES los datos del usuario (abajo). SIEMPRE salúdalo por su nombre. Responde con esos datos; usa el vehiculoId de la lista. Si una acción necesita carro y hay más de uno, pregunta cuál.

QUEJAS Y SUGERENCIAS: si el usuario se queja, reclama, protesta o propone una mejora del servicio o de la app, recoge lo que quiere decir y envíalo con enviarComentario (tipo "queja" o "sugerencia"). ANTES de llamar la función, REPÍTELE en voz alta el texto exacto que vas a enviar en su nombre y espera a que te diga que sí; aquí no hay pantalla de confirmación, tu voz es la única forma que tiene de saber qué se manda. No lo adornes ni lo suavices: manda lo que él dijo. Nunca digas que se envió sin haber llamado la función y recibido ok:true.

PUEDES EJECUTAR (confirma brevemente antes): actualizarKilometraje, registrarCombustible, registrarMantenimiento, registrarVehiculo (agregar un carro nuevo: pide marca y modelo; año, km y TIPO si los da; el TIPO es OPCIONAL, si no lo sabes registra igual y no bloquees el alta por eso; NUNCA digas que lo registraste sin llamar la función), agendarCita, ${EJECUTAR_ASISTENCIA}buscarRepuesto e irA. Para ${PANTALLA_ASISTENCIA}, la función lleva al usuario a la pantalla para terminar; avísale. Para diagnosticar, conversa tú mismo y SIEMPRE registra la consulta llamando guardarDiagnostico con un resumen (queda como CONSULTA en el carro, no como problema confirmado). Cuando ejecutes algo, narra el resultado.

REGLA ANTI-INVENCIÓN (crítica): NUNCA inventes. Solo afirma que hiciste algo (registrar carro, tanqueo, cita, etc.) si LLAMASTE la función correspondiente y devolvió ok:true; si no la llamaste, si falló, o si NO recibiste su resultado, NO digas que se hizo. Cuando la respuesta traiga el campo "instruccion", obedécela al pie de la letra. No inventes datos (precios, fechas, kilometrajes, confirmaciones, resultados): usa SOLO los datos de abajo y lo que devuelven las funciones. Si algo no lo puedes hacer con una función, dilo con honestidad en vez de fingir.

HONESTIDAD CON LAS ACCIONES (vale para TODAS, no solo para una): NUNCA afirmes que algo quedó hecho (registrado, guardado, agendado, actualizado, solicitado) si no recibiste el resultado confirmado de la función; sin resultado confirmado, la acción NO ocurrió y no puedes darla por hecha. Si una función devuelve un fallo o no recibes su resultado, dile al usuario la verdad, que NO se pudo completar y que NO se guardó nada, con el motivo en palabras de cliente (sin jerga técnica, sin códigos de error, sin culpar al usuario). Discúlpate con cortesía y ofrécele hacerlo él mismo desde la pantalla de la app que corresponde (sus vehículos, sus citas, el marketplace, etc.). Mantén el tono de asesor profesional de MekaFix TAMBIÉN cuando algo falla: no prometas lo que la app no hace, no inventes plazos ni soluciones, y no te salgas de los objetivos del servicio.

IMPORTANTE: diagnosticas VEHÍCULOS (carros, motos), NUNCA personas. JAMÁS digas que esto "no constituye un diagnóstico médico" ni recomiendes ir al médico — esto es mecánica automotriz, no salud.

${contextBlock || 'Sin datos del usuario.'}${tutorial ? TUTORIAL_EXTRA : ''}`;

  const semilla = typeof seedContext === 'string' ? seedContext.trim() : '';
  if (!semilla) return base;
  const reciente = semilla.length > SEED_MAX ? semilla.slice(-SEED_MAX) : semilla;

  return `${base}

CONVERSACIÓN QUE EL USUARIO TRAE DE LA APP (léela ANTES de hablar): venía conversando por escrito y quiere CONTINUAR por voz desde ese punto. Salúdalo, demuéstrale en una frase que ya sabes de qué venían hablando y sigue desde ahí. NO le pidas que repita lo que ya contó ni empieces de cero. Si ahí quedó algo pendiente de hacer, retómalo.
${reciente}`;
}

/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ 🔴 «CONECTANDO…» NO PUEDE SER PARA SIEMPRE.                               ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * `getUserMedia` no resuelve mientras el usuario no decida sobre el permiso del
 * micrófono: si deja el diálogo abierto, cambia de app o el navegador no llega a
 * mostrarlo, el arranque se queda colgado sin error y sin salida. El tope va
 * holgado a propósito (el token efímero sale de una función que puede arrancar
 * en frío), porque cortar una llamada legítima es peor que esperar un poco más.
 */
export const VOICE_START_TIMEOUT_MS = 30000;

/**
 * Por qué no arrancó la llamada, en la clave del mensaje que le toca al cliente.
 *
 * Antes todo caía en «el servicio de voz no está disponible», también el permiso
 * denegado, que es justo el único caso que el usuario arregla solo. Decirle que
 * el problema es nuestro cuando el arreglo es suyo lo deja esperando algo que no
 * va a llegar, y a la tercera vez concluye que la llamada no sirve.
 *
 * El motivo técnico NO sale de aquí: va al registro de la consola. Un código de
 * error en un aviso no le dice nada a quien tiene el carro dañado.
 */
export function clasificarErrorVoz(err) {
  const nombre = String(err?.name || '');
  const mensaje = String(err?.message || err || '');
  // Los nombres los fija la especificación de getUserMedia; `PermissionDenied*`
  // es el nombre viejo que todavía usan algunos WebView de Android.
  if (/^NotAllowedError$/i.test(nombre) || /^PermissionDenied/i.test(nombre)
      || /permission denied|permiso denegado/i.test(mensaje)) {
    return 'assistant.voiceMicDenied';
  }
  if (/^NotFoundError$/i.test(nombre) || /^DevicesNotFound/i.test(nombre)
      || /requested device not found/i.test(mensaje)) {
    return 'assistant.voiceNoMic';
  }
  return 'assistant.voiceUnavailable';
}

export function createLiveSession({ voice = 'Charon', userProfile, navigate, onStatus, onLine, onRemaining, tutorial = false, seedContext = '' }) {
  const r = {}; // refs internos
  const status = (s) => { try { onStatus && onStatus(s); } catch (_) {} };
  const line = (m) => { try { onLine && onLine(m); } catch (_) {} };

  const stop = () => {
    const consumed = r.consumed || 0;
    const wasLive = !!r.session;
    r.closing = true; // el close() de abajo dispara onclose: que no vuelva a entrar
    try { clearInterval(r.timer); } catch (_) {}
    try { r.proc && (r.proc.onaudioprocess = null); } catch (_) {}
    try { r.proc && r.proc.disconnect(); } catch (_) {}
    try { r.src && r.src.disconnect(); } catch (_) {}
    try { r.stream && r.stream.getTracks().forEach((t) => t.stop()); } catch (_) {}
    try { r.micCtx && r.micCtx.close(); } catch (_) {}
    try { r.session && r.session.close(); } catch (_) {}
    try { r.playCtx && r.playCtx.close(); } catch (_) {}
    Object.keys(r).forEach((k) => delete r[k]);
    try { onRemaining && onRemaining(null); } catch (_) {}
    status('idle');
    if (wasLive) { try { httpsCallable(functions, 'reportLiveUsage')({ seconds: consumed }); } catch (_) {} }
  };

  const start = async () => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    try {
      status('connecting');
      const playCtx = new AudioCtx();
      const micCtx = new AudioCtx();
      const resumeP = Promise.all([playCtx.resume(), micCtx.resume()]);
      const micP = navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      await resumeP;
      const stream = await micP;
      r.playCtx = playCtx; r.micCtx = micCtx; r.stream = stream;

      const tok = await httpsCallable(functions, 'liveToken')();
      const token = tok.data?.token;
      if (!token) throw new Error('Sin token');
      const maxSeconds = tok.data?.maxSeconds || 180;

      const { GoogleGenAI, Modality } = await import('@google/genai');
      const ai = new GoogleGenAI({ apiKey: token, httpOptions: { apiVersion: 'v1alpha' } });

      let ctx = { vehicles: [], alertsByVehicle: {}, recordsByVehicle: {} };
      let contextBlock = '';
      try { ctx = await buildAssistantContext(userProfile); contextBlock = buildContextBlock(ctx); } catch (_) {}
      r.ctx = ctx;

      // Referencia estable a la sesión para los callbacks. `stop()` vacía `r`, y
      // sin este respaldo un resultado que llega después del corte no tendría
      // por dónde salir NI por dónde avisar de que no salió.
      let sesionViva = null;
      const session = await ai.live.connect({
        model: 'gemini-3.1-flash-live-preview',
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: { languageCode: 'es-US', voiceConfig: { prebuiltVoiceConfig: { voiceName: voice } } },
          tools: [{ functionDeclarations: LIVE_TOOLS }],
          systemInstruction: buildLiveInstruction({ contextBlock, tutorial, seedContext }),
        },
        callbacks: {
          onopen: () => { status('live'); line('En vivo — habla'); },
          onmessage: (msg) => {
            const parts = msg.serverContent?.modelTurn?.parts || [];
            for (const p of parts) {
              if (p.inlineData?.data) playPcm24(playCtx, p.inlineData.data);
              if (p.text) line(p.text);
            }
            if (msg.toolCall?.functionCalls?.length) {
              const fcs = msg.toolCall.functionCalls;
              line('⚙️ ' + fcs.map((f) => f.name).join(', '));
              // `r.session` todavía puede no estar asignada (se pone al volver
              // de `connect`), así que se cae a la referencia estable: quedarse
              // sin ella era una de las formas de no entregar el resultado.
              responderLlamadas(fcs, {
                ctx: r.ctx || ctx,
                navigate,
                session: r.session || sesionViva,
                onLine: line,
              }).catch((e) => console.error('responderLlamadas:', e));
            }
          },
          onerror: () => { status('error'); stop(); },
          // Gemini cierra la sesión al vencer el token (la ventana de la llamada
          // la fija el servidor): sin esto la pantalla se quedaba «en vivo».
          onclose: () => { if (r.session && !r.closing) stop(); },
        },
      });
      r.session = session;
      sesionViva = session;

      try {
        session.sendClientContent({
          turns: [{ role: 'user', parts: [{ text: tutorial
            ? 'Comienza el tour de bienvenida ahora: salúdame con calidez, preséntate como mi asistente MekaFix y pregúntame cómo prefiero que me llamen.'
            : 'Inicié la llamada. Salúdame breve por mi nombre y pregunta en qué te ayudo con mi vehículo.' }] }],
          turnComplete: true,
        });
      } catch (_) {}

      const srcNode = micCtx.createMediaStreamSource(stream);
      const proc = micCtx.createScriptProcessor(4096, 1, 1);
      r.speakingUntil = 0;
      proc.onaudioprocess = (e) => {
        const modelActive = (r.playCtx?._nextTime || 0) > (r.playCtx?.currentTime || 0) + 0.05;
        if (modelActive) return; // no enviamos mic mientras el asistente habla (evita auto-corte)
        const input = e.inputBuffer.getChannelData(0);
        let sum = 0; for (let i = 0; i < input.length; i++) sum += input[i] * input[i];
        const rms = Math.sqrt(sum / input.length);
        const now = Date.now();
        if (rms > 0.012) { r.speakingUntil = now + 1500; }
        if (now < (r.speakingUntil || 0)) {
          try { session.sendRealtimeInput({ audio: { data: floatTo16kPcmBase64(input, micCtx.sampleRate), mimeType: 'audio/pcm;rate=16000' } }); } catch (_) {}
        }
      };
      srcNode.connect(proc); proc.connect(micCtx.destination);
      r.proc = proc; r.src = srcNode;

      // Saldo: baja solo cuando hay conversación; termina al agotarse.
      r.consumed = 0;
      r.lastReported = 0;
      try { onRemaining && onRemaining(maxSeconds); } catch (_) {}
      r.timer = setInterval(() => {
        const userActive = (r.speakingUntil || 0) > Date.now();
        const modelActive = (r.playCtx?._nextTime || 0) > (r.playCtx?.currentTime || 0) + 0.05;
        if (userActive || modelActive) {
          r.consumed = (r.consumed || 0) + 1;
          const left = maxSeconds - r.consumed;
          if (left <= 0) { stop(); return; }
          try { onRemaining && onRemaining(left); } catch (_) {}
          // Heartbeat: reporta lo hablado cada ~30s. Si la llamada muere sin
          // colgar (crash/cierre de tab), el backend liquida con este acumulado
          // en vez de dejar la reserva cobrada completa.
          if (r.consumed - (r.lastReported || 0) >= 30) {
            r.lastReported = r.consumed;
            try { httpsCallable(functions, 'reportLiveUsage')({ seconds: r.consumed, partial: true }); } catch (_) {}
          }
        }
      }, 1000);
    } catch (err) {
      line('Error: ' + (err?.message || err));
      status('error');
      stop();
      throw err;
    }
  };

  return { start, stop };
}
